import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, IconButton } from "@mui/material";
import {
  ArrowBackRounded,
  MicRounded,
  CallEndRounded,
  GraphicEqRounded,
} from "@mui/icons-material";
import { Room, RoomEvent, Track } from "livekit-client";
import { API_BASE, demo, glassCard } from "./demoStyles";

// idle -> connecting -> connected -> ended | error
const VoiceAssistant = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [speaking, setSpeaking] = useState(null); // 'agent' | 'user' | null
  const [error, setError] = useState("");
  const [transcript, setTranscript] = useState([]); // {id, role, text, final}

  const roomRef = useRef(null);
  const roomNameRef = useRef(null);
  const audioElsRef = useRef([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript]);

  const cleanup = useCallback(async () => {
    const room = roomRef.current;
    if (room) {
      try { await room.disconnect(); } catch { /* ignore */ }
      roomRef.current = null;
    }
    audioElsRef.current.forEach((el) => { try { el.remove(); } catch { /* ignore */ } });
    audioElsRef.current = [];
    if (roomNameRef.current) {
      // best-effort end of the Lyzr session
      fetch(`${API_BASE}/demo/voice/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomName: roomNameRef.current }),
      }).catch(() => {});
      roomNameRef.current = null;
    }
  }, []);

  // Disconnect/end if the user leaves the page
  useEffect(() => () => { cleanup(); }, [cleanup]);

  const upsertSegments = (segments, role) => {
    setTranscript((prev) => {
      const next = [...prev];
      segments.forEach((seg) => {
        const idx = next.findIndex((t) => t.id === seg.id);
        const entry = { id: seg.id, role, text: seg.text, final: !!seg.final };
        if (idx >= 0) next[idx] = entry; else next.push(entry);
      });
      return next;
    });
  };

  const startCall = useCallback(async () => {
    setError("");
    setTranscript([]);
    setStatus("connecting");
    try {
      const res = await fetch(`${API_BASE}/demo/voice/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not start the voice session.");

      const { livekitUrl, userToken, roomName } = data;
      if (!livekitUrl || !userToken) throw new Error("Voice session response was incomplete.");
      roomNameRef.current = roomName;

      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room
        .on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Audio) {
            const el = track.attach();
            el.autoplay = true;
            el.style.display = "none";
            document.body.appendChild(el);
            audioElsRef.current.push(el);
            el.play?.().catch(() => {});
          }
        })
        .on(RoomEvent.TranscriptionReceived, (segments, participant) => {
          const role = participant && participant.isLocal ? "user" : "agent";
          upsertSegments(segments, role);
        })
        .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          if (!speakers || speakers.length === 0) { setSpeaking(null); return; }
          const agent = speakers.find((p) => !p.isLocal);
          const local = speakers.find((p) => p.isLocal);
          setSpeaking(agent ? "agent" : local ? "user" : null);
        })
        .on(RoomEvent.Disconnected, () => {
          setStatus("ended");
          setSpeaking(null);
        });

      await room.connect(livekitUrl, userToken);
      await room.localParticipant.setMicrophoneEnabled(true);
      setStatus("connected");
    } catch (err) {
      await cleanup();
      setError(err.message || "Something went wrong starting the call.");
      setStatus("error");
    }
  }, [cleanup]);

  const endCall = useCallback(async () => {
    await cleanup();
    setStatus("ended");
    setSpeaking(null);
  }, [cleanup]);

  const isLive = status === "connected";
  const statusText = {
    idle: "Tap to start talking with ServoSaathi",
    connecting: "Connecting…",
    connected: speaking === "agent" ? "ServoSaathi is speaking…"
      : speaking === "user" ? "Listening…"
      : "Connected — go ahead, I'm listening",
    ended: "Call ended",
    error: error || "Something went wrong",
  }[status];

  // Orb visual state
  const orbActive = speaking === "agent";
  const orbListening = speaking === "user";

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", background: demo.bgGradient, display: "flex", flexDirection: "column" }}>
      {/* Top bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: { xs: 2.5, md: 5 }, py: 2 }}>
        <IconButton onClick={() => navigate("/demo")} sx={{ color: demo.textDim }}>
          <ArrowBackRounded />
        </IconButton>
        <Box>
          <Typography sx={{ color: demo.text, fontWeight: 800, fontSize: 18, lineHeight: 1 }}>
            ServoSaathi Voice
          </Typography>
          <Typography sx={{ color: demo.textDim, fontSize: 12, mt: 0.3 }}>
            Talk to your assistant about pumps & EV chargers
          </Typography>
        </Box>
      </Box>

      {/* Center stage */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: 2, pb: 2 }}>
        {/* Orb */}
        <Box sx={{ position: "relative", width: 220, height: 220, display: "flex", alignItems: "center", justifyContent: "center", mb: 4 }}>
          {/* pulse rings */}
          {(orbActive || orbListening) && [0, 1, 2].map((i) => (
            <Box key={i} sx={{
              position: "absolute", width: "100%", height: "100%", borderRadius: "50%",
              border: `2px solid ${orbActive ? demo.accent : demo.green}`,
              animation: "voicePulse 2s ease-out infinite",
              animationDelay: `${i * 0.5}s`,
              "@keyframes voicePulse": {
                "0%": { transform: "scale(0.7)", opacity: 0.7 },
                "100%": { transform: "scale(1.25)", opacity: 0 },
              },
            }} />
          ))}
          <Box sx={{
            width: 150, height: 150, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: orbActive
              ? `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`
              : isLive
                ? `linear-gradient(135deg, #1f2a44, #243049)`
                : `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`,
            boxShadow: orbActive
              ? `0 0 60px ${demo.accent}`
              : isLive ? "0 10px 40px rgba(0,0,0,0.5)" : `0 10px 40px rgba(34,211,238,0.4)`,
            transition: "all 0.3s ease",
            transform: orbActive ? "scale(1.06)" : "scale(1)",
          }}>
            {isLive
              ? <GraphicEqRounded sx={{ fontSize: 60, color: "#fff" }} />
              : <MicRounded sx={{ fontSize: 60, color: "#fff" }} />}
          </Box>
        </Box>

        <Typography sx={{ color: demo.text, fontSize: 17, fontWeight: 600, textAlign: "center", minHeight: 26 }}>
          {statusText}
        </Typography>

        {/* Transcript */}
        {transcript.length > 0 && (
          <Box ref={scrollRef} sx={{
            ...glassCard, mt: 3, width: "100%", maxWidth: 560, maxHeight: 220, overflowY: "auto",
            p: 2, display: "flex", flexDirection: "column", gap: 1,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255,255,255,0.12)", borderRadius: "3px" },
          }}>
            {transcript.map((t) => (
              <Box key={t.id} sx={{ alignSelf: t.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
                <Typography sx={{ fontSize: 11, color: demo.textFaint, mb: 0.2, textAlign: t.role === "user" ? "right" : "left" }}>
                  {t.role === "user" ? "You" : "ServoSaathi"}
                </Typography>
                <Box sx={{
                  px: 1.5, py: 0.9, borderRadius: t.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  background: t.role === "user" ? `linear-gradient(135deg, ${demo.primary}, ${demo.primaryDeep})` : demo.surface,
                  border: t.role === "user" ? "none" : `1px solid ${demo.border}`,
                  color: t.role === "user" ? "#fff" : demo.text, fontSize: 13.5, lineHeight: 1.45,
                  opacity: t.final ? 1 : 0.7,
                }}>
                  {t.text}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Controls */}
        <Box sx={{ mt: 4 }}>
          {!isLive && status !== "connecting" ? (
            <Button
              onClick={startCall}
              startIcon={<MicRounded />}
              sx={{
                px: 4, py: 1.4, borderRadius: "999px", fontSize: 16, fontWeight: 700, textTransform: "none",
                color: "#fff", background: `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`,
                boxShadow: `0 10px 30px rgba(34,211,238,0.35)`,
                "&:hover": { background: `linear-gradient(135deg, ${demo.primaryDeep}, ${demo.accent})` },
              }}
            >
              {status === "ended" || status === "error" ? "Start a new call" : "Start call"}
            </Button>
          ) : (
            <IconButton
              onClick={endCall}
              disabled={status === "connecting"}
              sx={{
                width: 66, height: 66, color: "#fff", background: "#ef4444",
                boxShadow: "0 10px 30px rgba(239,68,68,0.45)",
                "&:hover": { background: "#dc2626" },
                "&.Mui-disabled": { background: "rgba(255,255,255,0.1)", color: demo.textFaint },
              }}
            >
              <CallEndRounded sx={{ fontSize: 30 }} />
            </IconButton>
          )}
        </Box>

        {status === "error" && (
          <Typography sx={{ color: "#f87171", fontSize: 13, mt: 2, textAlign: "center", maxWidth: 420 }}>
            {error} — please check your microphone permission and try again.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default VoiceAssistant;
